/**
 * Núcleo do produto — cockpit 1.2 e busca global 2.2 (passo 4.8).
 *
 * O cockpit recebe agregados servidos: o cliente não soma dinheiro nem reconstrói
 * calendário. Na busca, cada item traz apenas identidade e contexto de navegação;
 * valores financeiros não viajam numa superfície cujo trabalho é encontrar fichas.
 */
import { z } from 'zod'
import { codigoDeMoeda, dataNominal, uuid } from './comum.js'
import { atingimento, previsao } from './metas.js'
import { horizonteProjetadoDeCaixa } from './tesouraria.js'

/**
 * Os oito grupos da busca 2.2 estão definidos no enum abaixo e na view cumulativa.
 * Fornecedor entrou na 0058, campanha na 0064, colaborador na 0068 e tarefa na 0072.
 * O controlador só consulta os grupos permitidos ao nível autenticado.
 *
 * O id de `fornecedor` é o da CAPACIDADE, e não o da empresa: é ele que `6.6?id=`
 * resolve, e é o único que endereça também o fornecedor pessoa física.
 */
export const tipoDeResultadoDaBusca = z.enum([
  'empresa', 'contato', 'negocio', 'pedido', 'fornecedor', 'campanha', 'colaborador', 'tarefa',
])
export type TipoDeResultadoDaBusca = z.infer<typeof tipoDeResultadoDaBusca>

export const itemDaBuscaGlobal = z.object({
  id: uuid,
  tipo: tipoDeResultadoDaBusca,
  titulo: z.string(),
  subtitulo: z.string(),
})
export type ItemDaBuscaGlobal = z.infer<typeof itemDaBuscaGlobal>

export const grupoDaBuscaGlobal = z.object({
  tipo: tipoDeResultadoDaBusca,
  itens: z.array(itemDaBuscaGlobal),
})
export type GrupoDaBuscaGlobal = z.infer<typeof grupoDaBuscaGlobal>

export const respostaDaBuscaGlobal = z.object({
  grupos: z.array(grupoDaBuscaGlobal),
  total: z.number().int().nonnegative(),
})
export type RespostaDaBuscaGlobal = z.infer<typeof respostaDaBuscaGlobal>

export const indicadorDoCockpit = z.object({
  /** `null` = o nível pode contar registros, mas não ver montantes. */
  valor: z.string().nullable(),
  quantidade: z.number().int().nonnegative(),
  /** Percentual já calculado pelo servidor; `null` = sem base comparável. */
  variacaoPct: z.string().nullable(),
})
export type IndicadorDoCockpit = z.infer<typeof indicadorDoCockpit>

export const pontoDosTresCalendarios = z.object({
  mes: dataNominal,
  vendas: z.string().nullable(),
  faturamento: z.string().nullable(),
  recebido: z.string().nullable(),
})
export type PontoDosTresCalendarios = z.infer<typeof pontoDosTresCalendarios>

/**
 * O faturamento mínimo OPERACIONAL (D-180, DS-626): antes de D&A, do resultado financeiro e do IRPJ/CSLL, sem
 * investimento. `valor` é a média mensal no período; `total`, o do período inteiro (a soma, mês a mês, dos fixos × a
 * fração do mês ÷ o índice do exercício); `meses`, a soma das frações. `fonte` continua a primeira versão; `fontes`
 * traz uma por exercício. Os campos novos são opcionais: a API anterior não os manda.
 */
export const breakEven = z.object({
  valor: z.string().nullable(),
  /** Faturamento realizado no período ÷ o mínimo do período; servido, nunca recalculado pela tela. */
  atingimentoPct: z.string().nullable(),
  estado: z.enum(['calculado', 'sem_orcamento_aprovado', 'sem_margem_positiva', 'sem_custos_fixos']),
  fonte: z.object({
    versaoId: uuid,
    versaoRotulo: z.string(),
    ano: z.number().int(),
    competencia: dataNominal,
  }).nullable(),
  total: z.string().nullable().optional(),
  meses: z.string().optional(),
  /** O índice de margem de contribuição do exercício, quando o período é de um exercício só. */
  indiceMc: z.string().nullable().optional(),
  fontes: z.array(z.object({
    versaoId: uuid,
    versaoRotulo: z.string(),
    ano: z.number().int(),
    indiceMc: z.string().nullable(),
  })).optional(),
})
export type BreakEven = z.infer<typeof breakEven>

export const cockpit = z.object({
  usuarioNome: z.string(),
  entidadeNome: z.string(),
  moeda: codigoDeMoeda,
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  atualizadoEm: z.string(),
  temMovimento: z.boolean(),
  /** Bloco `null` = o módulo-fonte não é visível para o nível. */
  vendas: indicadorDoCockpit.nullable(),
  faturamento: indicadorDoCockpit.nullable(),
  recebido: indicadorDoCockpit.nullable(),
  /**
   * A AGENDA do período: o que vencia entre `periodoDe` e `periodoAte`, contra o que
   * entrou. É a base da barra emerald do KPI "Recebido no período" (spec 1.2:36).
   *
   * Bloco à parte, e não campo dentro de `recebido`: `indicadorDoCockpit` é a MESMA
   * forma de vendas e faturamento, e um campo de agenda ali nasceria nulo para os
   * outros dois para sempre. `esperado` nulo = o nível conta mas não vê montantes;
   * `atingimentoPct` nulo = não havia vencimento no período ("sem agenda no período").
   */
  agenda: z.object({
    esperado: z.string().nullable(),
    quantidade: z.number().int().nonnegative(),
    /** Recebido ÷ esperado, em pontos percentuais. Servido, nunca recalculado pela tela. */
    atingimentoPct: z.string().nullable(),
  }).nullable(),
  fila: z.object({
    valor: z.string().nullable(),
    quantidade: z.number().int().nonnegative(),
    maisAntigo: z.object({ pedidoId: uuid, codigo: z.string(), dias: z.number().int().nonnegative() }).nullable(),
  }).nullable(),
  vencidos: z.object({
    valor: z.string().nullable(),
    /** DS496: saldo legado não quantificado; diferente de valor oculto por permissão. */
    requerConferencia: z.boolean().optional(),
    quantidade: z.number().int().nonnegative(),
    maisCritico: z.object({ pedidoId: uuid, codigo: z.string(), dias: z.number().int().nonnegative() }).nullable(),
  }).nullable(),
  /** Realizado de hoje; a curva projetada completa pertence à 6.1 e à 6.2. */
  caixa: z.object({
    saldo: z.string().nullable(),
    /** Mesma pendência prospectiva da agenda; não muda o saldo bancário conhecido. */
    requerConferencia: z.boolean().optional(),
    /** COMPATIBILIDADE: os meses do `horizonte`, com o nome antigo. */
    runwayMeses: z.number().nullable(),
    menorSaldo: z.string().nullable(),
    menorSaldoEm: dataNominal.nullable(),
    /**
     * O horizonte projetado de caixa (DS-580), o MESMO objeto que a 6.2 e a 6.1 recebem da
     * curva projetada. Nulo quando não é calculado: sem acesso a valores, ou na visão
     * consolidada, em que a curva é por entidade (DS-584).
     */
    horizonte: horizonteProjetadoDeCaixa.nullable(),
  }).nullable(),
  mrr: z.object({ valor: z.string().nullable(), quantidade: z.number().int().nonnegative() }).nullable(),
  faturamentoPorProduto: z.array(z.object({
    produtoId: uuid,
    produtoNome: z.string(),
    valor: z.string().nullable(),
  })),
  breakEven: breakEven.nullable(),
  meta: atingimento.nullable(),
  previsao: previsao.nullable(),
  serie: z.array(pontoDosTresCalendarios),
})
export type Cockpit = z.infer<typeof cockpit>
